import { first, isDate, last } from "lodash-es";
import moment from "moment-timezone";
import { DateRange } from "react-big-calendar";

import { CalendarView } from "../components/Calendar/Calendar";
import { assertNever } from "../ts/typeHelpers";
import { TypeHelper } from "./type";

export class CalendarHelper {
  /** Min date supported by calendar component. */
  public static get minDate(): Date {
    return new Date(1970, 0, 1, 0, 0, 0, 0);
  }

  /** Max date supported by calendar component. */
  public static get maxDate(): Date {
    return new Date(5000, 0, 1, 0, 0, 0, 0);
  }

  public static getViewDateRangeInclusive(
    view: CalendarView,
    range: Date | Date[] | { start: Date; end: Date },
  ): DateRange {
    const rangeAnchorDate = isDate(range) ? range : undefined;
    const rangeArray = !isDate(range) && TypeHelper.isArray(range) ? range : [];
    const rangeObj = !isDate(range) && !TypeHelper.isArray(range) ? range : ({} as DateRange);

    switch (view) {
      case CalendarView.Month: {
        return rangeAnchorDate
          ? {
              start: moment(rangeAnchorDate).startOf("month").toDate(),
              end: moment(rangeAnchorDate).endOf("month").toDate(),
            }
          : {
              start: moment(rangeObj.start).startOf("day").toDate(),
              end: moment(rangeObj.end).endOf("day").toDate(),
            };
      }
      case CalendarView.Week: {
        return rangeAnchorDate
          ? {
              start: moment(rangeAnchorDate).startOf("week").toDate(),
              end: moment(rangeAnchorDate).endOf("week").toDate(),
            }
          : {
              start: moment(first(rangeArray)!).startOf("day").toDate(),
              end: moment(last(rangeArray)!).endOf("day").toDate(),
            };
      }
      case CalendarView.WorkWeek: {
        return rangeAnchorDate
          ? {
              start: moment(rangeAnchorDate).startOf("isoWeek").toDate(),
              end: moment(rangeAnchorDate).add(4, "days").endOf("day").toDate(),
            }
          : {
              start: moment(first(rangeArray)!).startOf("day").toDate(),
              end: moment(last(rangeArray)!).endOf("day").toDate(),
            };
      }
      case CalendarView.Day: {
        return rangeAnchorDate
          ? {
              start: moment(rangeAnchorDate).startOf("day").toDate(),
              end: moment(rangeAnchorDate).endOf("day").toDate(),
            }
          : {
              start: moment(rangeArray[0]!).startOf("day").toDate(),
              end: moment(rangeArray[0]!).endOf("day").toDate(),
            };
      }
      case CalendarView.Agenda: {
        return rangeAnchorDate
          ? {
              start: moment(rangeAnchorDate).startOf("day").toDate(),
              end: moment(rangeAnchorDate).add(30, "days").toDate(),
            }
          : {
              start: moment(rangeObj.start).startOf("day").toDate(),
              end: moment(rangeObj.end).endOf("day").toDate(),
            };
      }
      case CalendarView.Year: {
        const baseDate = rangeAnchorDate
          ? moment(rangeAnchorDate)
          : rangeObj.start
            ? moment(rangeObj.start)
            : moment();
        return {
          start: baseDate.clone().startOf("year").toDate(),
          end: baseDate.clone().endOf("year").toDate(),
        };
      }
      default:
        assertNever(view); // TS error if case missing
    }
  }

  public static getViewDateRangeExclusiveFromInclusive(
    view: CalendarView,
    rangeInclusive: DateRange,
  ): DateRange {
    switch (view) {
      case CalendarView.Month:
      case CalendarView.Week:
      case CalendarView.WorkWeek:
      case CalendarView.Day:
      case CalendarView.Agenda:
      case CalendarView.Year: {
        return {
          start: moment(rangeInclusive.start).startOf("day").toDate(),
          end: moment(rangeInclusive.end).add(1, "day").startOf("day").toDate(),
        };
      }
      default:
        assertNever(view); // TS error if case missing
    }
  }
}
